import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { Stack, Text } from '@solvhealth/jigsaw-v1';
import ClinicClosed from './ClinicClosed';
import NoSlotsLeft from './NoSlotsLeft';
import { getCurrentStateOfLocationSlots } from '../../../util';
import Loading from '../../../../SolvPatternLibrary/Loading';
import { colors } from '../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { getRelativeDayName, RelativeDayName } from '../../../../../core/util/date';
import {
  isBeyondNextDayAppointmentsEnabled,
  isHomeHealthcareLocation,
} from '../../../../../core/util/location';
import { getCurrentPath } from '../../../../../core/util/url';
import { isBookingWidget } from '../../../../../core/opengraph';
import AtHomeCareSlotDisclaimer from '../../../../ClinicDetailPageFlexible/components/BookModule/components/AtHomeCareSlotDisclaimer';
import Slots from '~/components/BookingTimeSelector/Slots';
import useDefaultSlot from '~/components/BookingTimeSelector/hooks/useDefaultSlot';
import useDaypartingSlots from '~/components/BookingTimeSelector/hooks/useDaypartingSlots';
import { filterSlotsByAvailability } from '~/core/location/slots';
import ReducedTimeSlotGrid from './ReducedTimeSlotGrid';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';

const SlotGrid = styled(ReducedTimeSlotGrid).withConfig({ componentId: 'sc-1rlce9w-0' })`
  justify-content: center;
`;

const SpacedLoading = styled.div.withConfig({ componentId: 'sc-1rlce9w-1' })`
  text-align: center;
  height: 108px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const StyledNoNextAvailableVisitText = styled.div.withConfig({ componentId: 'sc-1rlce9w-2' })`
  color: ${colors.blueMonday};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  background-color: ${colors.karl};
  letter-spacing: -0.25px;
  line-height: 20px;
  text-align: left;
  padding: 10px 20px;
  margin: 15px 0px -20px;
`;

type Props = {
  futureSlotData: any;
  handleSelectSlot: (...args: any[]) => any;
  location: any;
  nextDate: any;
  selectedDate: any;
  selectedSlots: any[];
  shouldShowWalkInCopy?: boolean;
  setAndHandleDate?: (...args: any[]) => any;
  handleClickShowMore?: (...args: any[]) => any;
  isCTA?: boolean;
  //  Below are for old booking widget only
  allowExpandInPlace: boolean;
  shouldSelectFirstSlotAsInitialValue: boolean;
  shouldSetSelectedSlotToFirstVisibleSlot: boolean;
  shouldShowNextAvailableAppointmentCta: boolean;
};

const SlotSelectorStates = (props: Props) => {
  const {
    futureSlotData,
    handleSelectSlot,
    location,
    nextDate,
    selectedDate,
    selectedSlots,
    shouldShowWalkInCopy,
    setAndHandleDate,
    handleClickShowMore,
    isCTA = false,
    //  Below are for old booking flow only
    allowExpandInPlace,
    shouldSelectFirstSlotAsInitialValue,
    shouldSetSelectedSlotToFirstVisibleSlot,
    shouldShowNextAvailableAppointmentCta,
  } = props;

  const flags = useLDFlags();
  const { t } = useTranslation();

  const closedWarningCopy = () => (
    <StyledNoNextAvailableVisitText>
      {t('bookingTimeSelector.slotSelectorStates.onlineBookingOnlyTodayOrTomorrow')}
    </StyledNoNextAvailableVisitText>
  );

  const isBW = isBookingWidget(getCurrentPath());

  const shouldShowClosedWarning = () =>
    getRelativeDayName(selectedDate, location.timezone) === RelativeDayName.TOMORROW &&
    !isBeyondNextDayAppointmentsEnabled(location) &&
    isBW;

  const {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'isClinicClosed' does not exist on type '... Remove this comment to see the full error message
    isClinicClosed,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'nextDateSlots' does not exist on type 'E... Remove this comment to see the full error message
    nextDateSlots,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'selectedDateHasNoSlotsLeft' does not exi... Remove this comment to see the full error message
    selectedDateHasNoSlotsLeft,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'selectedDateSlots' does not exist on typ... Remove this comment to see the full error message
    selectedDateSlots,
  } = getCurrentStateOfLocationSlots(futureSlotData, location, selectedDate);

  const availableSelectedDateSlots = filterSlotsByAvailability(selectedDateSlots);

  useDefaultSlot({
    handleSelectSlot,
    location,
    selectedSlots,
    slots: availableSelectedDateSlots,
    disabled: isCTA,
  });

  const { morningSlots, afternoonSlots, eveningSlots } = useDaypartingSlots({
    timeZone: location.timeZone,
    slots: availableSelectedDateSlots,
  });

  if (isClinicClosed) {
    return (
      <>
        <ClinicClosed
          handleClickShowMore={handleClickShowMore}
          isBookingWidget={isBW}
          location={location}
          nextDate={nextDate}
          nextDateSlots={nextDateSlots}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          setAndHandleDate={setAndHandleDate}
          shouldShowNextAvailableAppointmentCta={shouldShowNextAvailableAppointmentCta}
          shouldShowWalkInCopy={shouldShowWalkInCopy}
        />
        {shouldShowClosedWarning() && closedWarningCopy()}
      </>
    );
  }

  if (selectedDateHasNoSlotsLeft) {
    return (
      <>
        <NoSlotsLeft
          handleClickShowMore={handleClickShowMore}
          handleSelectSlot={handleSelectSlot}
          isBookingWidget={isBW}
          location={location}
          selectedDate={selectedDate}
          setAndHandleDate={setAndHandleDate}
          shouldShowNextAvailableAppointmentCta={shouldShowNextAvailableAppointmentCta}
        />
        {shouldShowClosedWarning() && closedWarningCopy()}
      </>
    );
  }

  if (!selectedDateSlots) {
    return (
      <SpacedLoading>
        <Loading color={colors.crunchBerry} />
      </SpacedLoading>
    );
  }

  return (
    <>
      {isHomeHealthcareLocation(location) && <AtHomeCareSlotDisclaimer />}
      {flags.kis511H22022BookingFlow ? (
        <Stack pt={1} space={3}>
          {!isEmpty(morningSlots) && (
            <div>
              <Text color="gray-800" pb={2} textAlign="center" variant="subtitle2">
                Morning
              </Text>
              <Slots
                handleSelectSlot={handleSelectSlot}
                isCTA={isCTA}
                location={location}
                selectedSlots={selectedSlots}
                slots={morningSlots}
                slotsHeight={32}
                slotsWidth={70}
              />
            </div>
          )}
          {!isEmpty(afternoonSlots) && (
            <div>
              <Text color="gray-800" pb={2} textAlign="center" variant="subtitle2">
                Afternoon
              </Text>
              <Slots
                handleSelectSlot={handleSelectSlot}
                isCTA={isCTA}
                location={location}
                selectedSlots={selectedSlots}
                slots={afternoonSlots}
                slotsHeight={32}
                slotsWidth={70}
              />
            </div>
          )}
          {!isEmpty(eveningSlots) && (
            <div>
              <Text color="gray-800" pb={2} textAlign="center" variant="subtitle2">
                Evening and after-hours
              </Text>
              <Slots
                handleSelectSlot={handleSelectSlot}
                isCTA={isCTA}
                location={location}
                selectedSlots={selectedSlots}
                slots={eveningSlots}
                slotsHeight={32}
                slotsWidth={70}
              />
            </div>
          )}
        </Stack>
      ) : (
        <SlotGrid
          allowExpandInPlace={allowExpandInPlace}
          handleSelectSlot={handleSelectSlot}
          hasSelectedDateFromModal={!isEmpty(selectedDate)}
          location={location}
          selectedSlots={selectedSlots}
          shouldSelectFirstSlotAsInitialValue={shouldSelectFirstSlotAsInitialValue}
          shouldSetSelectedSlotToFirstVisibleSlot={shouldSetSelectedSlotToFirstVisibleSlot}
          slots={selectedDateSlots}
          timeZone={location.timeZone}
        />
      )}
    </>
  );
};

export default SlotSelectorStates;
